/**
 * UTF-8 by hand, in both directions.
 *
 * `TextEncoder`/`TextDecoder` would be shorter, and the reason not to use them
 * is not purity: the decoder here has to *fail* on malformed bytes rather than
 * repair them. `TextDecoder` without `fatal` substitutes U+FFFD for every bad
 * sequence, which turns a damaged record into a well-formed string that then
 * fails to parse as JSON somewhere else, and the recovery row ends up blaming
 * the wrong step. With `fatal` it throws, and a throw inside the decode
 * pipeline is exactly the shape this package spent `JournalFileRead` avoiding.
 *
 * So: encode is total, decode returns `undefined` for bytes that are not valid
 * UTF-8, and "not valid" is the strict definition — no overlong forms, no
 * surrogate code points, nothing above U+10FFFF. A lenient decoder would accept
 * two different byte strings as the same record, and this package content-hashes
 * records.
 */

const REPLACEMENT = 0xfffd;

/**
 * Text to bytes.
 *
 * A lone surrogate becomes U+FFFD rather than a WTF-8 three-byte sequence. It
 * is unreachable for the text this package writes — canonical JSON escapes lone
 * surrogates as `\uXXXX` before they ever reach here — but a total function may
 * not depend on that, and emitting bytes the matching decoder would reject
 * would make a write fail its own verification for a reason nothing could
 * explain.
 */
export function journalUtf8Encode(text: string): Uint8Array {
  const bytes: number[] = [];
  for (let index = 0; index < text.length; index += 1) {
    const unit = text.charCodeAt(index);
    let point = unit;

    if (unit >= 0xd800 && unit <= 0xdbff) {
      const low = index + 1 < text.length ? text.charCodeAt(index + 1) : 0;
      if (low >= 0xdc00 && low <= 0xdfff) {
        point = 0x10000 + ((unit - 0xd800) << 10) + (low - 0xdc00);
        index += 1;
      } else {
        point = REPLACEMENT;
      }
    } else if (unit >= 0xdc00 && unit <= 0xdfff) {
      point = REPLACEMENT;
    }

    if (point < 0x80) {
      bytes.push(point);
    } else if (point < 0x800) {
      bytes.push(0xc0 | (point >> 6), 0x80 | (point & 0x3f));
    } else if (point < 0x10000) {
      bytes.push(
        0xe0 | (point >> 12),
        0x80 | ((point >> 6) & 0x3f),
        0x80 | (point & 0x3f),
      );
    } else {
      bytes.push(
        0xf0 | (point >> 18),
        0x80 | ((point >> 12) & 0x3f),
        0x80 | ((point >> 6) & 0x3f),
        0x80 | (point & 0x3f),
      );
    }
  }
  return Uint8Array.from(bytes);
}

/** Bytes to text, or `undefined` when the bytes are not valid UTF-8. */
export function journalUtf8Decode(bytes: Uint8Array): string | undefined {
  const units: number[] = [];
  let index = 0;

  while (index < bytes.length) {
    const lead = bytes[index];
    if (lead === undefined) return undefined;
    index += 1;

    let point: number;
    let continuations: number;
    let lowerBound: number;

    if (lead < 0x80) {
      units.push(lead);
      continue;
    } else if (lead >= 0xc2 && lead <= 0xdf) {
      point = lead & 0x1f;
      continuations = 1;
      lowerBound = 0x80;
    } else if (lead >= 0xe0 && lead <= 0xef) {
      point = lead & 0x0f;
      continuations = 2;
      lowerBound = 0x800;
    } else if (lead >= 0xf0 && lead <= 0xf4) {
      point = lead & 0x07;
      continuations = 3;
      lowerBound = 0x10000;
    } else {
      // 0x80–0xbf is a stray continuation; 0xc0/0xc1 can only ever encode an
      // overlong ASCII byte; 0xf5 and above are past U+10FFFF.
      return undefined;
    }

    for (let step = 0; step < continuations; step += 1) {
      const next = bytes[index];
      if (next === undefined || next < 0x80 || next > 0xbf) return undefined;
      point = (point << 6) | (next & 0x3f);
      index += 1;
    }

    // Overlong forms and surrogate code points decode to something a shorter or
    // different sequence already represents; accepting them would let two byte
    // strings hash differently while reading as the same record.
    if (point < lowerBound) return undefined;
    if (point > 0x10ffff) return undefined;
    if (point >= 0xd800 && point <= 0xdfff) return undefined;

    if (point < 0x10000) {
      units.push(point);
    } else {
      const offset = point - 0x10000;
      units.push(0xd800 + (offset >> 10), 0xdc00 + (offset & 0x3ff));
    }
  }

  // Chunked so a large record cannot exhaust the argument limit of `apply`.
  const CHUNK = 4096;
  let text = "";
  for (let start = 0; start < units.length; start += CHUNK) {
    text += String.fromCharCode(...units.slice(start, start + CHUNK));
  }
  return text;
}
